import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Alert } from '../../../components/ui/Alert';
import { formatCentsToBRL } from '../../../utils/money';
import type { Offer, Quote } from '../../../lib/api-client';

/**
 * Renders a `Quote`'s offers (or the empty/partial-degradation states) —
 * extracted from onboarding's `DemoStep.tsx` (TTP-287) so `/quotes`
 * (post-onboarding) shows the exact same comparison UI instead of a second
 * copy. `quote.partial`/`failed_providers` are surfaced the same way in
 * both callers: a non-alarming warning, never a blocking error (CLAUDE.md
 * domain rule #6 — "degradar, não falhar").
 */
export function QuoteResult({ quote }: { quote: Quote }) {
  if (quote.offers.length === 0) {
    return (
      <div aria-live="polite">
        <Alert variant="warning" title="Nenhuma oferta disponível">
          Nenhum provedor retornou uma oferta para esse trajeto agora. Tente outro CEP ou volte mais tarde.
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" aria-live="polite">
      {/* Visually redundant with the cards below, but the only way a screen
          reader gets notified the async quote finished (ux-ui-reviewer
          finding, TTP-287) — the grid itself has no text node to announce. */}
      <p className="sr-only">{quote.offers.length} oferta{quote.offers.length > 1 ? 's' : ''} encontrada{quote.offers.length > 1 ? 's' : ''}.</p>

      {quote.partial ? (
        <Alert variant="warning" title="Resposta parcial">
          Algum provedor não respondeu a tempo:{' '}
          {quote.failed_providers.map((f) => f.provider).join(', ')}. As demais ofertas abaixo continuam válidas.
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {quote.offers.map((offer) => (
          <OfferCard key={offer.offer_id} offer={offer} />
        ))}
      </div>
    </div>
  );
}

function OfferCard({ offer }: { offer: Offer }) {
  return (
    <Card
      className={`flex flex-col gap-3 p-5 ${offer.recommended ? 'border-primary ring-1 ring-primary' : ''}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">
          {offer.provider}
        </span>
        {offer.recommended ? <Badge variant="success">Recomendada</Badge> : null}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-lg font-medium text-foreground">{offer.carrier}</span>
        <span className="text-sm text-muted-foreground">{offer.service}</span>
        {offer.recommended && offer.recommendation_reason ? (
          <span className="text-xs text-muted-foreground">{offer.recommendation_reason}</span>
        ) : null}
      </div>
      <div className="mt-auto flex items-end justify-between">
        <span className="text-2xl font-medium text-foreground">{formatCentsToBRL(offer.price_cents)}</span>
        <span className="text-sm text-muted-foreground">
          {offer.delivery_days_min}–{offer.delivery_days_max} dias úteis
        </span>
      </div>
    </Card>
  );
}
